import { useState, useRef, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  X,
  ChevronRight,
  GripVertical,
  Trash2,
  Plus,
  ChevronUp,
  ChevronDown,
  Clock,
  Loader2,
  Settings,
} from "lucide-react"
import ProfileSidebar from "./ProfileSideBar"
import InterviewSettingsModal from "./InterviewSettingsModal"
import toast from "react-hot-toast"

// ── Number stepper ────────────────────────────────────────────────────────────
const NumberStepper = ({ value, onChange, min = 1, max = 99 }) => {
  const formatted = String(value).padStart(2, "0")
  return (
    <div
      className="flex items-center w-full border border-zinc-200 bg-white overflow-hidden"
      style={{ borderRadius: 0 }}
    >
      <div
        className="flex-1 px-3 py-2 text-sm text-zinc-800 select-none"
        style={{ fontFamily: "'IBM Plex Mono', monospace" }}
      >
        {formatted}
      </div>
      <div className="flex flex-col border-l border-zinc-200 flex-shrink-0">
        <button
          onMouseDown={(e) => {
            e.preventDefault()
            if (value < max) onChange(value + 1)
          }}
          className="flex items-center justify-center w-7 h-[18px] hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors border-b border-zinc-200"
        >
          <ChevronUp size={10} strokeWidth={2.5} />
        </button>
        <button
          onMouseDown={(e) => {
            e.preventDefault()
            if (value > min) onChange(value - 1)
          }}
          className="flex items-center justify-center w-7 h-[18px] hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors"
        >
          <ChevronDown size={10} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  )
}

const SECTION_DOT = {
  introduction: "bg-emerald-400",
  conclusion: "bg-violet-400",
  custom: "bg-zinc-400",
}

const inferSectionType = (name = "") => {
  const lower = name.toLowerCase().trim()
  if (lower === "introduction") return "introduction"
  if (lower === "conclusion") return "conclusion"
  return "custom"
}

const buildSectionsFromRound = (round, nextIdRef) => {
  if (!round?.sections?.length) {
    return [
      {
        id: nextIdRef.current++,
        type: "introduction",
        label: "Introduction",
        duration: 5,
        instructions: "",
      },
      {
        id: nextIdRef.current++,
        type: "conclusion",
        label: "Conclusion",
        duration: 5,
        instructions: "",
      },
    ]
  }

  return round.sections.map((sec) => {
    const type = inferSectionType(sec.name)
    return {
      id: nextIdRef.current++,
      type,
      label: sec.name,
      duration: sec.duration ?? 15,
      numQuestions: sec.numQuestions ?? 5,
      instructions: sec.instruction ?? "",
    }
  })
}

const isAiRound = (roundType = "") => {
  const lower = (roundType ?? "").toLowerCase().trim()
  return lower === "ai"
}

// ── Section detail panel ──────────────────────────────────────────────────────
const SectionDetailPanel = ({ section, onChange }) => {
  if (!section) {
    return (
      <div className="flex-1 flex items-center justify-center bg-zinc-50">
        <p
          className="text-xs text-zinc-300 font-medium"
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
        >
          Select a section to configure
        </p>
      </div>
    )
  }

  const isFixed =
    section.type === "introduction" || section.type === "conclusion"
  const dotColor = SECTION_DOT[section.type] ?? "bg-zinc-400"

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 bg-white shrink-0">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${dotColor}`} />
          <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">
            {section.label}
          </span>
          {isFixed && (
            <span
              className="text-[10px] text-zinc-300 border border-zinc-200 px-1.5 py-0.5 font-medium"
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                borderRadius: 0,
              }}
            >
              FIXED
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto hide-scroll bg-zinc-50 px-6 py-6">
        <div className="flex flex-col gap-6 max-w-lg">
          {!isFixed && (
            <>
              <div className="flex flex-col gap-2">
                <label
                  className="text-[11px] font-bold text-zinc-700 uppercase tracking-wide"
                  style={{ letterSpacing: "0.05em" }}
                >
                  Section Name
                </label>
                <input
                  type="text"
                  value={section.label}
                  onChange={(e) =>
                    onChange({ ...section, label: e.target.value })
                  }
                  className="w-full px-3 py-2.5 text-[13px] text-zinc-700 border border-zinc-200 bg-white focus:outline-none focus:border-zinc-400"
                  style={{
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    borderRadius: 0,
                  }}
                />
              </div>
              <div className="border-t border-zinc-200" />
            </>
          )}

          <div className="flex items-start gap-8">
            <div className="flex flex-col gap-2">
              <label
                className="text-[11px] font-bold text-zinc-700 uppercase tracking-wide"
                style={{ letterSpacing: "0.05em" }}
              >
                Duration (mins)
              </label>
              <NumberStepper
                value={section.duration}
                onChange={(v) => onChange({ ...section, duration: v })}
                min={1}
                max={120}
              />
            </div>
            {!isFixed && (
              <div className="flex flex-col gap-2">
                <label
                  className="text-[11px] font-bold text-zinc-700 uppercase tracking-wide"
                  style={{ letterSpacing: "0.05em" }}
                >
                  No. of Questions
                </label>
                <NumberStepper
                  value={section.numQuestions ?? 5}
                  onChange={(v) => onChange({ ...section, numQuestions: v })}
                  min={1}
                  max={50}
                />
              </div>
            )}
          </div>

          <div className="border-t border-zinc-200" />

          <div className="flex flex-col gap-2">
            <label
              className="text-[11px] font-bold text-zinc-700 uppercase tracking-wide"
              style={{ letterSpacing: "0.05em" }}
            >
              Instructions{" "}
              <span className="text-zinc-400 normal-case font-normal tracking-normal">
                (Optional)
              </span>
            </label>
            <textarea
              value={section.instructions ?? ""}
              onChange={(e) =>
                onChange({ ...section, instructions: e.target.value })
              }
              placeholder={
                section.type === "introduction"
                  ? "E.g. Greet the candidate, explain the interview format, set expectations…"
                  : section.type === "conclusion"
                    ? "E.g. Thank the candidate, explain next steps, invite questions…"
                    : "E.g. Focus on past experience, ask for specific examples…"
              }
              rows={5}
              className="w-full px-3 py-2.5 text-[13px] text-zinc-700 border border-zinc-200 bg-white resize-none focus:outline-none focus:border-zinc-400 placeholder-zinc-300 leading-relaxed"
              style={{
                fontFamily: "'IBM Plex Sans', sans-serif",
                borderRadius: 0,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Section row ───────────────────────────────────────────────────────────────
const SectionRow = ({
  section,
  isActive,
  onClick,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}) => {
  const isFixed =
    section.type === "introduction" || section.type === "conclusion"
  const dotColor = SECTION_DOT[section.type] ?? "bg-zinc-400"

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 text-left px-3 py-2.5 mb-1 border transition-all group
        ${isActive ? "bg-blue-50 border-blue-200" : "bg-white border-zinc-100 hover:border-zinc-300"}`}
      style={{ borderRadius: 0 }}
    >
      {isFixed ? (
        <div className="w-4 flex-shrink-0 flex items-center justify-center">
          <div className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
        </div>
      ) : (
        <div className="w-4 flex-shrink-0 flex items-center justify-center text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity">
          <GripVertical size={12} />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span
            className={`text-xs font-bold uppercase tracking-widest truncate ${isActive ? "text-blue-700" : "text-zinc-500"}`}
          >
            {section.label}
          </span>
          {isFixed && (
            <span
              className="text-[9px] text-zinc-300 font-medium flex-shrink-0"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              FIXED
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          <Clock size={8} className="text-zinc-300 flex-shrink-0" />
          <span
            className="text-[10px] text-zinc-400"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            {section.duration} mins
          </span>
        </div>
      </div>

      {!isFixed && (
        <div
          className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          {canMoveUp && (
            <button
              onClick={onMoveUp}
              className="flex items-center justify-center w-5 h-5 text-zinc-300 hover:text-zinc-600 transition-colors"
              title="Move up"
            >
              <ChevronUp size={10} strokeWidth={2.5} />
            </button>
          )}
          {canMoveDown && (
            <button
              onClick={onMoveDown}
              className="flex items-center justify-center w-5 h-5 text-zinc-300 hover:text-zinc-600 transition-colors"
              title="Move down"
            >
              <ChevronDown size={10} strokeWidth={2.5} />
            </button>
          )}
          <button
            onClick={onDelete}
            className="flex items-center justify-center w-5 h-5 text-zinc-300 hover:text-red-500 transition-colors"
            title="Delete section"
          >
            <Trash2 size={10} />
          </button>
        </div>
      )}
    </button>
  )
}

// ── RoundPanel: manages sections for a single round ───────────────────────────
const RoundPanel = ({ round, onUpdate, interviewSettings, onOpenSettings }) => {
  const nextId = useRef(100 + round.id * 100)
  const [sections, setSections] = useState(round.sections)
  const [activeId, setActiveId] = useState(round.sections[0]?.id ?? null)

  const updateSections = (newSections) => {
    setSections(newSections)
    onUpdate({ ...round, sections: newSections })
  }

  const activeSection = sections.find((s) => s.id === activeId) ?? null

  const updateSection = (updated) => {
    const next = sections.map((s) => (s.id === updated.id ? updated : s))
    updateSections(next)
  }

  const addSection = () => {
    const id = nextId.current++
    const newSection = {
      id,
      type: "custom",
      label: "Custom Section",
      duration: 15,
      numQuestions: 5,
      instructions: "",
    }
    const conclusionIdx = sections.findIndex((s) => s.type === "conclusion")
    const next = [...sections]
    if (conclusionIdx >= 0) {
      next.splice(conclusionIdx, 0, newSection)
    } else {
      next.push(newSection)
    }
    updateSections(next)
    setActiveId(id)
  }

  const deleteSection = (id) => {
    const next = sections.filter((s) => s.id !== id)
    updateSections(next)
    if (activeId === id) setActiveId(next[0]?.id ?? null)
  }

  const moveSection = (id, dir) => {
    const idx = sections.findIndex((s) => s.id === id)
    const targetIdx = idx + dir
    if (targetIdx < 0 || targetIdx >= sections.length) return
    if (
      sections[targetIdx]?.type === "introduction" ||
      sections[targetIdx]?.type === "conclusion"
    )
      return
    const next = [...sections]
    ;[next[idx], next[targetIdx]] = [next[targetIdx], next[idx]]
    updateSections(next)
  }

  const totalDuration = sections.reduce((acc, s) => acc + (s.duration || 0), 0)

  const getMoveConstraints = (section, idx) => {
    if (section.type === "introduction" || section.type === "conclusion")
      return { canMoveUp: false, canMoveDown: false }
    return {
      canMoveUp: sections[idx - 1]?.type !== "introduction",
      canMoveDown: sections[idx + 1]?.type !== "conclusion",
    }
  }

  return (
    <div className="flex flex-1 min-h-0">
      {/* LEFT PANEL */}
      <div
        className="flex flex-col border-r border-zinc-200 bg-white shrink-0"
        style={{ width: "40%" }}
      >
        {/* Interview Settings row */}
        <div className="mx-0 border-b border-zinc-100">
          <button
            type="button"
            onClick={onOpenSettings}
            className="w-full flex items-start justify-between px-5 py-4 text-left hover:bg-zinc-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 border border-zinc-200 flex items-center justify-center flex-shrink-0 bg-zinc-50">
                <Settings size={13} className="text-zinc-500" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-zinc-800">
                    Interview Settings
                  </span>
                  {interviewSettings.duration > 0 && (
                    <span
                      className="text-[10px] text-zinc-400 font-medium"
                      style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                    >
                      ⊙ {interviewSettings.duration} Mins
                      {interviewSettings.skills.length > 0 &&
                        ` · ${interviewSettings.skills.length} skill${interviewSettings.skills.length > 1 ? "s" : ""}`}
                    </span>
                  )}
                </div>
                {interviewSettings.skills.length > 0 ? (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {interviewSettings.skills.map((skill) => (
                      <span
                        key={skill}
                        className="text-[10px] font-bold uppercase tracking-widest text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5"
                        style={{
                          borderRadius: 0,
                          fontFamily: "'IBM Plex Mono', monospace",
                        }}
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-zinc-400 mt-0.5 leading-snug">
                    Duration and skills to evaluate
                  </p>
                )}
              </div>
            </div>
            <ChevronRight
              size={13}
              className="text-zinc-300 mt-0.5 flex-shrink-0"
            />
          </button>
        </div>

        {/* Sections list */}
        <div className="flex flex-col flex-1 min-h-0 pt-4">
          <div className="px-5 mb-3 flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
              Round Sections
            </span>
            <span
              className="text-[10px] text-zinc-300"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              {sections.length}
            </span>
          </div>

          <div className="flex-1 overflow-y-auto hide-scroll px-3">
            {sections.map((section, idx) => {
              const { canMoveUp, canMoveDown } = getMoveConstraints(
                section,
                idx,
              )
              return (
                <SectionRow
                  key={section.id}
                  section={section}
                  isActive={activeId === section.id}
                  onClick={() => setActiveId(section.id)}
                  onDelete={() => deleteSection(section.id)}
                  onMoveUp={() => moveSection(section.id, -1)}
                  onMoveDown={() => moveSection(section.id, 1)}
                  canMoveUp={canMoveUp}
                  canMoveDown={canMoveDown}
                />
              )
            })}
          </div>

          <div className="px-3 py-4 border-t border-zinc-100">
            <button
              onClick={addSection}
              className="w-full flex items-center gap-2 px-3 py-2.5 border border-dashed border-zinc-300 text-zinc-400 hover:border-zinc-600 hover:text-zinc-700 transition-all"
              style={{ borderRadius: 0 }}
            >
              <Plus size={11} />
              <span className="text-xs font-semibold">Add section</span>
            </button>
          </div>
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
        <SectionDetailPanel section={activeSection} onChange={updateSection} />
      </div>
    </div>
  )
}

// ── Helper: build a fresh round object ───────────────────────────────────────
const createFreshRound = (roundNumber, globalNextId) => {
  const introId = globalNextId.current++
  const concId = globalNextId.current++
  return {
    id: roundNumber,
    roundType: "Human+Ai",
    sections: [
      {
        id: introId,
        type: "introduction",
        label: "Introduction",
        duration: 5,
        instructions: "",
      },
      {
        id: concId,
        type: "conclusion",
        label: "Conclusion",
        duration: 5,
        instructions: "",
      },
    ],
  }
}

// ── Build rounds from API interviewStructure ──────────────────────────────────
const buildRoundsFromStructure = (interviewStructure, globalNextId) => {
  if (!interviewStructure?.length) {
    return [createFreshRound(1, globalNextId)]
  }

  return interviewStructure.map((round, idx) => {
    const roundType = round.roundType ?? "Human+Ai"
    const aiRound = isAiRound(roundType)
    const sections = aiRound ? [] : buildSectionsFromRound(round, globalNextId)
    return {
      id: idx + 1,
      roundId: round.roundId,
      roundType,
      sections,
    }
  })
}

// ── Page component ────────────────────────────────────────────────────────────
const AddInterviewRoundStructure = () => {
  const { orgId, jobId } = useParams()
  const navigate = useNavigate()

  const globalNextId = useRef(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  const [isFetching, setIsFetching] = useState(true)
  const [fetchError, setFetchError] = useState(null)
  const [jobName, setJobName] = useState("Job")

  const [rounds, setRounds] = useState([])
  const [activeRoundId, setActiveRoundId] = useState(null)

  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [roundSettings, setRoundSettings] = useState({})

  // ── Fetch job data on mount ─────────────────────────────────────────────────
  useEffect(() => {
    const fetchJobData = async () => {
      setIsFetching(true)
      setFetchError(null)
      try {
        const response = await fetch(
          `https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${orgId}/${jobId}/lite`,
        )
        if (!response.ok) {
          throw new Error(`Failed to fetch job data (${response.status})`)
        }
        const data = await response.json()

        const jobData = data.data ?? data
        setJobName(
          jobData.role ??
            jobData.jobTitle ??
            jobData.jobName ??
            jobData.title ??
            "Job",
        )

        const structureRounds =
          jobData.interviewStructure?.rounds ??
          (Array.isArray(jobData.interviewStructure)
            ? jobData.interviewStructure
            : null)

        const builtRounds = buildRoundsFromStructure(
          structureRounds,
          globalNextId,
        )
        setRounds(builtRounds)
        const firstEditable = builtRounds.find((r) => !isAiRound(r.roundType))
        setActiveRoundId(firstEditable?.id ?? null)

        const seeded = {}
        builtRounds
          .filter((r) => !isAiRound(r.roundType))
          .forEach((r, idx) => {
            const rawRound = structureRounds?.[idx]
            seeded[r.id] = {
              duration:
                rawRound?.duration ??
                r.sections?.reduce((acc, s) => acc + (s.duration || 0), 0) ??
                0,
              skills: rawRound?.skillsToEvaluate ?? [],
            }
          })
        setRoundSettings(seeded)
      } catch (err) {
        setFetchError(err.message ?? "Failed to load job data.")
        const fallback = [createFreshRound(1, globalNextId)]
        setRounds(fallback)
        setActiveRoundId(1)
      } finally {
        setIsFetching(false)
      }
    }

    if (jobId && orgId) {
      fetchJobData()
    } else {
      const fallback = [createFreshRound(1, globalNextId)]
      setRounds(fallback)
      setActiveRoundId(1)
      setIsFetching(false)
    }
  }, [jobId, orgId])

  const activeRound = rounds.find((r) => r.id === activeRoundId) ?? rounds[0]

  const activeRoundSettings = roundSettings[activeRoundId] ?? {
    duration: 0,
    skills: [],
  }

  const updateRoundSettings = (settings) => {
    setRoundSettings((prev) => ({ ...prev, [activeRoundId]: settings }))
  }

  const addRound = () => {
    const maxId = rounds.length ? Math.max(...rounds.map((r) => r.id)) : 0
    const newRound = createFreshRound(maxId + 1, globalNextId)
    setRounds((prev) => [...prev, newRound])
    setActiveRoundId(newRound.id)
  }

  const deleteRound = (roundId) => {
    if (rounds.length === 1) return
    const next = rounds.filter((r) => r.id !== roundId)
    setRounds(next)
    if (activeRoundId === roundId) {
      setActiveRoundId(next[0].id)
    }
  }

  const updateRound = (updatedRound) => {
    setRounds((prev) =>
      prev.map((r) => (r.id === updatedRound.id ? updatedRound : r)),
    )
  }

  // ── API submit ──────────────────────────────────────────────────────────────
  const handleSetupRound = async () => {
    // Skills validation
    const roundsMissingSkills = rounds
      .filter((r) => !isAiRound(r.roundType))
      .filter((r) => (roundSettings[r.id]?.skills ?? []).length < 3)

    if (roundsMissingSkills.length > 0) {
      toast.error(
        `Please add at least 3 skills for: ${roundsMissingSkills.map((_, i) => `Round ${i + 1}`).join(", ")}`,
        {
          className:
            "!font-mono !text-[11px] !rounded-none !font-medium !tracking-wide",
        },
      )
      return
    }

    // Duration mismatch validation
    const roundsWithMismatch = rounds
      .filter((r) => !isAiRound(r.roundType))
      .filter((r) => {
        const sectionTotal = r.sections.reduce(
          (acc, s) => acc + (s.duration || 0),
          0,
        )
        const settingsDuration = roundSettings[r.id]?.duration ?? 0
        return settingsDuration > 0 && sectionTotal !== settingsDuration
      })

    if (roundsWithMismatch.length > 0) {
      const details = roundsWithMismatch
        .map((r, i) => {
          const sectionTotal = r.sections.reduce(
            (acc, s) => acc + (s.duration || 0),
            0,
          )
          const settingsDuration = roundSettings[r.id]?.duration ?? 0
          return `Round ${i + 1}: sections total ${sectionTotal} min, settings set to ${settingsDuration} min`
        })
        .join(" · ")
      toast.error(`Duration mismatch — ${details}`, {
        className:
          "!font-mono !text-[11px] !rounded-none !font-medium !tracking-wide",
      })
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)

    const payload = rounds
      .filter((round) => !isAiRound(round.roundType))
      .map((round, idx) => ({
        roundId: round.roundId ?? `round-${idx + 1}`,
        roundType: round.roundType ?? "Human+Ai",
        duration:
          roundSettings[round.id]?.duration ??
          round.sections.reduce((acc, s) => acc + (s.duration || 0), 0),
        skillsToEvaluate: roundSettings[round.id]?.skills ?? [],
        sections: round.sections.map((s) => ({
          name: s.label,
          duration: s.duration,
          instruction: s.instructions ?? "",
        })),
      }))

    try {
      const response = await fetch(
        `https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${orgId}/${jobId}/create-interview-structure`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ rounds: payload }),
        },
      )

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(
          `Request failed with status ${response.status}: ${errorText}`,
        )
      }

      await response.json()
      navigate("/jobs")
    } catch (err) {
      setSubmitError(err.message ?? "Something went wrong. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  // ── Loading state ───────────────────────────────────────────────────────────
  if (isFetching) {
    return (
      <div
        className="min-h-screen bg-zinc-50 flex"
        style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
      >
        <style>{`
      @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
    `}</style>
        <div className="border-r border-zinc-200 h-full bg-white overflow-y-auto min-h-screen">
          <ProfileSidebar activeTab="jobs" setActiveTab={() => {}} />
        </div>
        <div className="flex-1 ml-60 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 size={20} className="animate-spin text-zinc-400" />
            <p
              className="text-xs text-zinc-400"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              Loading job structure…
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="min-h-screen bg-zinc-50 flex"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <style>{`
      @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
      .hide-scroll::-webkit-scrollbar { display: none; }
      .hide-scroll { -ms-overflow-style: none; scrollbar-width: none; }
    `}</style>

      {/* Sidebar */}
      <div className="border-r border-zinc-200 bg-white overflow-y-auto min-h-screen">
        <ProfileSidebar activeTab="jobs" setActiveTab={() => {}} />
      </div>

      {/* Main content */}
      <div className="flex-1 ml-60 flex flex-col overflow-hidden h-screen">
        {/* Top bar */}
        <div className="bg-white px-8 py-4 flex items-center justify-between border-b border-zinc-200 shrink-0">
          <div className="flex items-center gap-3">
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-bold text-zinc-900">
                  Interview Structure
                </p>
                <span
                  className="text-[9px] font-bold uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5"
                  style={{ borderRadius: 0 }}
                >
                  Editing
                </span>
              </div>
              <p className="text-xs text-zinc-400">{jobName}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {fetchError && (
              <p
                className="text-[11px] text-amber-500 max-w-xs"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                ⚠ {fetchError} — using defaults
              </p>
            )}
            <button
              onClick={() => navigate("/jobs")}
              className="p-1.5 hover:bg-zinc-100 rounded-lg transition-colors text-zinc-400 hover:text-zinc-700"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        // Replace the Round Tabs section:
        {/* Round Tabs */}
        <div className="flex items-center gap-0 border-b border-zinc-200 bg-white px-6 shrink-0 overflow-x-auto hide-scroll">
          {rounds.filter((r) => !isAiRound(r.roundType)).length === 0 ? (
            // Placeholder tab when no editable rounds exist
            <button
              onClick={addRound}
              className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold border-b-2 border-zinc-900 text-zinc-900 whitespace-nowrap"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-zinc-900" />
              round-1
            </button>
          ) : (
            rounds
              .filter((r) => !isAiRound(r.roundType))
              .map((round, idx) => {
                const isActive = round.id === activeRoundId
                return (
                  <div
                    key={round.id}
                    className="flex items-center group relative"
                  >
                    <button
                      onClick={() => setActiveRoundId(round.id)}
                      className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold transition-all border-b-2 whitespace-nowrap
                ${
                  isActive
                    ? "border-zinc-900 text-zinc-900"
                    : "border-transparent text-zinc-400 hover:text-zinc-600 hover:border-zinc-300"
                }`}
                      style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isActive ? "bg-zinc-900" : "bg-zinc-300"}`}
                      />
                      round-{idx + 1}
                    </button>
                    {rounds.filter((r) => !isAiRound(r.roundType)).length >
                      1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          deleteRound(round.id)
                        }}
                        className="absolute right-0.5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center w-4 h-4 text-zinc-300 hover:text-red-400"
                        title="Remove round"
                      >
                        <X size={9} strokeWidth={2.5} />
                      </button>
                    )}
                  </div>
                )
              })
          )}

          <button
            onClick={addRound}
            className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-zinc-400 hover:text-zinc-700 border-b-2 border-transparent transition-all whitespace-nowrap ml-1"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            <Plus size={11} strokeWidth={2.5} />
            Add round
          </button>
        </div>
        {/* Round panel body */}
        <div className="flex flex-1 min-h-0">
          {rounds.filter((r) => !isAiRound(r.roundType)).length === 0 ? (
            // Empty state — shown under the placeholder round-1 tab
            <div className="flex-1 flex items-center justify-center bg-zinc-50">
              <div className="flex flex-col items-center gap-3 text-center px-8">
                <div className="w-10 h-10 rounded-full bg-zinc-100 border border-zinc-200 flex items-center justify-center">
                  <Plus size={16} className="text-zinc-400" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-1">
                    No rounds configured
                  </p>
                  <p
                    className="text-[11px] text-zinc-400 max-w-xs leading-relaxed"
                    style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                  >
                    Click "Add round" to configure your first interview round
                    with sections.
                  </p>
                </div>
                <button
                  onClick={addRound}
                  className="mt-1 flex items-center gap-2 px-4 py-2 border border-dashed border-zinc-300 text-zinc-400 hover:border-zinc-600 hover:text-zinc-700 transition-all text-xs font-semibold"
                  style={{ borderRadius: 0 }}
                >
                  <Plus size={11} />
                  Add round
                </button>
              </div>
            </div>
          ) : activeRound && !isAiRound(activeRound.roundType) ? (
            <RoundPanel
              key={activeRound.id}
              round={activeRound}
              onUpdate={updateRound}
              interviewSettings={activeRoundSettings}
              onOpenSettings={() => setShowSettingsModal(true)}
            />
          ) : null}
        </div>
        {/* Footer */}
        <div className="px-8 py-4 border-t border-zinc-200 bg-white flex items-center justify-between gap-4 shrink-0">
          <div className="flex items-center gap-4">
            {submitError && (
              <p
                className="text-[11px] text-red-500 max-w-sm"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                {submitError}
              </p>
            )}
            {!submitError && (
              <p
                className="text-[11px] text-zinc-400"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                {rounds.length} round{rounds.length !== 1 ? "s" : ""} configured
                {rounds.some((r) => isAiRound(r.roundType)) && (
                  <span className="ml-2 text-zinc-300">
                    · {rounds.filter((r) => isAiRound(r.roundType)).length}{" "}
                    AI-managed
                  </span>
                )}
              </p>
            )}
          </div>

          <button
            onClick={handleSetupRound}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-5 py-2.5 text-xs font-bold tracking-wide transition-all border bg-zinc-900 border-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ borderRadius: 0 }}
          >
            {isSubmitting ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                Saving…
              </>
            ) : (
              <>
                Save Changes
                <ChevronRight size={12} />
              </>
            )}
          </button>
        </div>
      </div>
      {showSettingsModal && (
        <InterviewSettingsModal
          interviewMode="human"
          initialDuration={activeRoundSettings.duration}
          initialSkills={activeRoundSettings.skills}
          onSave={updateRoundSettings}
          onClose={() => setShowSettingsModal(false)}
        />
      )}
    </div>
  )
}

export default AddInterviewRoundStructure
